import { AppError } from "../app-error";
import type { UploadProgress } from "./types";

/** The signed request an upload is made with: its address and the headers its signature binds. */
export type PutTarget = { url: string; headers: Readonly<Record<string, string>> };

export type Put = (
  target: PutTarget,
  body: Blob,
  options: { signal: AbortSignal; onProgress: UploadProgress },
) => Promise<void>;

/** An upload link is good for 15 minutes; past that the storage service refuses the signature. */
const PUT_TIMEOUT_MS = 15 * 60_000;

/**
 * Sends the bytes straight to storage with the headers the API signed, and reports how far they
 * have got. `XMLHttpRequest` is what reports upload progress; `fetch` cannot. A failure is the
 * app's own error, never the storage service's words: a refusal of the signature (an upload that
 * waited past its link, or a type that was changed) and a failure to connect are both "try again".
 */
export const putWithProgress: Put = (target, body, { signal, onProgress }) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Canceled", "AbortError"));
      return;
    }
    const request = new XMLHttpRequest();
    const onAbort = () => {
      request.abort();
    };
    const finish = () => {
      signal.removeEventListener("abort", onAbort);
    };
    request.open("PUT", target.url);
    // Without a deadline a stalled connection holds the upload, and its slot, until it is canceled.
    request.timeout = PUT_TIMEOUT_MS;
    for (const [name, value] of Object.entries(target.headers))
      request.setRequestHeader(name, value);
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    request.onload = () => {
      finish();
      if (request.status >= 200 && request.status < 300) {
        onProgress(1);
        resolve();
      } else {
        reject(new AppError("SERVICE_UNAVAILABLE", { status: request.status }));
      }
    };
    request.onerror = () => {
      finish();
      reject(new AppError("NETWORK_ERROR"));
    };
    request.ontimeout = () => {
      finish();
      reject(new AppError("REQUEST_TIMEOUT"));
    };
    request.onabort = () => {
      finish();
      reject(new DOMException("Canceled", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
    request.send(body);
  });
